/**
 * NZSC-3054: 셜록 체납∙연체 조회 Vuex 모듈
 *
 * - 법인: 동의 불필요, 즉시 조회
 * - 개인: 알림톡(`R000000044_23440`) 발송 후 대상자 동의 → 결과 조회
 * - 포인트 차감 API 호출은 비활성 (NZSC-3047 셜록 구독형 가격제와 합쳐 활성)
 *
 * 격리: 도비/카우 코드 import 금지. 셜록 모듈 내부에서 완결.
 */
import SherlockAcConstant from "@/mixins/sherlock/const/sherlockAcConstant.js";
import SherlockConsentText from "@/mixins/sherlock/const/sherlockConsentTextConstant.js";
import { callPostApi, callGetApi } from "@/utils/apiWrapper";
import api from "@/utils/api";

// NZSC-3120 — Content-Disposition 헤더의 RFC 5987 인코딩 파일명 추출.
// filename*=UTF-8''{encoded} 우선, 없으면 filename="..." fallback.
function fnParseDispositionFilename(disposition) {
  if (!disposition) return null;
  const rfc5987 = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (rfc5987) {
    try {
      return decodeURIComponent(rfc5987[1]);
    } catch (e) {
      return null;
    }
  }
  const fallback = disposition.match(/filename="([^"]+)"/);
  if (fallback) {
    try {
      return decodeURIComponent(fallback[1]);
    } catch (e) {
      return fallback[1];
    }
  }
  return null;
}

export const state = {
  // 법인 조회 결과 (즉시 조회)
  _corporateResult: null,
  // 개인 조회 결과 (동의 후 확인 가능)
  _individualResult: null,
  // 개인 동의 요청 상태
  _individualConsentRequest: null, // { token, status, requestedAt }
  // 조회/발급 이력 리스트
  _historyList: [],
  _historyPaging: { page: 1, totalCount: 0, pageSize: 20 },
  // 외부 동의 페이지용 (피조회자가 보는 화면)
  _consentInfo: null, // { requesterName, targetName, scope }
  // NZSC-3074: KCB 본인인증 게스트 흐름 결과 — prepare/guest 응답 보관.
  //  { popup_url, cp_cd, token (mdl_tkn) } — KcbUserVerify 패턴과 동일.
  _verifyPrepare: null,
  _loading: false,
};

export const mutations = {
  SET_CORPORATE_RESULT(state, data) {
    state._corporateResult = data;
  },
  SET_INDIVIDUAL_RESULT(state, data) {
    state._individualResult = data;
  },
  SET_INDIVIDUAL_CONSENT_REQUEST(state, data) {
    state._individualConsentRequest = data;
  },
  SET_HISTORY_LIST(state, { items, totalCount, page, pageSize }) {
    state._historyList = items || [];
    state._historyPaging = {
      page: page || 1,
      totalCount: totalCount || 0,
      pageSize: pageSize || 20,
    };
  },
  SET_CONSENT_INFO(state, data) {
    state._consentInfo = data;
  },
  SET_VERIFY_PREPARE(state, data) {
    state._verifyPrepare = data;
  },
  SET_LOADING(state, val) {
    state._loading = val;
  },
  RESET_CORPORATE(state) {
    state._corporateResult = null;
  },
  RESET_INDIVIDUAL(state) {
    state._individualResult = null;
    state._individualConsentRequest = null;
  },
};

export const getters = {
  getCorporateResult: state => state._corporateResult,
  getIndividualResult: state => state._individualResult,
  getHistoryList: state => state._historyList,
};

export const actions = {
  /**************************************************************
   * 법인 즉시 조회
   * 입력: { corpName, corpRegNo }
   *
   * NOTE: 포인트 차감 로직은 BE NZSC-3053 가 처리하나, 이번 PR은
   * 셜록 구독형 가격제(NZSC-3047) 와 합쳐 최종 오픈 — 현재는 UI 만
   * 동작하며 차감 비활성 (BE 가 dry-run 모드로 동작 가정).
   **************************************************************/
  async acQueryCorporate({ commit }, { corpName, corpRegNo }) {
    commit("SET_LOADING", true);
    // [hotfix 2026-05-19] BE DTO 정본 필드명 corporateName/corporateRegNo 매칭.
    const res = await callPostApi({
      url: SherlockAcConstant.POST_SHERLOCK_CREDIT_CORPORATE,
      data: { corporateName: corpName, corporateRegNo: corpRegNo },
      commit,
    });
    commit("SET_LOADING", false);
    if (res) {
      commit("SET_CORPORATE_RESULT", res);
    }
    return res;
  },

  /**************************************************************
   * 개인 동의 요청 알림톡 발송
   * 입력: { name, phone } → BE NZSC-3057 contract: { subjectName, subjectPhone }
   * 출력: { token, status: 'PENDING', sherlockCreditQuerySeq } — 알림톡 발송 후 대상자가 동의해야 결과 확인 가능
   *
   * NZSC-3058: BE endpoint 는 POST /v1/sherlock/credit/individual/request (consent-request 가 아님).
   * payload 는 BE DTO 필드명에 맞춰 subjectName/subjectPhone 으로 전송.
   **************************************************************/
  async acRequestIndividualConsent({ commit }, { name, phone }) {
    commit("SET_LOADING", true);
    const res = await callPostApi({
      url: SherlockAcConstant.POST_SHERLOCK_CREDIT_INDIVIDUAL_REQUEST,
      data: { subjectName: name, subjectPhone: phone },
      commit,
    });
    commit("SET_LOADING", false);
    if (res) {
      commit("SET_INDIVIDUAL_CONSENT_REQUEST", res);
    }
    return res;
  },

  /**************************************************************
   * 개인 동의 상태 확인 (요청자 화면에서 폴링)
   *
   * NZSC-3058: BE NZSC-3057 contract 정렬.
   * BE 에는 별도 GET status endpoint 가 없고, confirm endpoint(POST /individual/confirm)
   * 가 status='preparing'/'completed'/'expired' 를 반환. FE 폴링도 동일 endpoint 사용.
   *
   * confirm 응답 status:
   *  - 'preparing'  : 개인 흐름 비활성 (SHERLOCK_CREDIT_INDIVIDUAL_ENABLED=false) — NZSC-3047 합본 시 활성
   *  - 'completed'  : 동의 완료 + cold-syrup 조회 성공 → result 포함
   *  - (UnauthorizedException: token expired/used/invalid → 401)
   **************************************************************/
  async acGetIndividualConsentStatus({ commit }, { token }) {
    const res = await callPostApi({
      url: SherlockAcConstant.POST_SHERLOCK_CREDIT_INDIVIDUAL_CONFIRM,
      data: { token },
      commit,
    });
    if (res?.status === "completed" && res?.result) {
      commit("SET_INDIVIDUAL_RESULT", res.result);
    }
    return res;
  },

  /**************************************************************
   * 외부 동의 페이지 — 대상자가 토큰으로 진입.
   *
   * NZSC-3059: BE GET /v1/sherlock/credit/consent/{token} 신설.
   *  - 200: { subjectName, expiresDate } — 유효 토큰 (미사용 + 미만료)
   *  - 404: 무효 토큰 (DB 미존재)
   *  - 410: 만료 또는 사용 완료 (단건 사용 정책)
   *
   * 응답의 subjectName 으로 SET_CONSENT_INFO 의 targetName 을 채움.
   * apiWrapper 가 4xx 시 null 반환 → caller(ConsentExternalView)가 'error' step.
   *
   * 호환성: 응답에 subjectName 없으면 fallback 빈 문자열 유지 (caller 가 "고객님" 표시).
   **************************************************************/
  async acGetConsentInfo({ commit }, { token }) {
    if (!token) return null;
    // GET /credit/consent/:token
    const res = await callGetApi({
      url: `${SherlockAcConstant.GET_SHERLOCK_CREDIT_CONSENT}/${encodeURIComponent(token)}`,
      commit,
    });
    if (!res) {
      // 404/410/네트워크 실패 — apiWrapper 가 null 반환. caller 가 'error' step 으로.
      return null;
    }
    commit("SET_CONSENT_INFO", {
      requesterName: SherlockConsentText.REQUESTER_NAME,
      targetName: res.subjectName || "",
      scopeText: SherlockConsentText.SCOPE_TEXT,
      expiresDate: res.expiresDate || null,
      status: "PENDING",
    });
    return { status: "PENDING", subjectName: res.subjectName, expiresDate: res.expiresDate };
  },

  /**************************************************************
   * 외부 동의 페이지 — 약관 동의 완료 처리.
   *
   * NZSC-3058: BE NZSC-3057 에 별도 agree endpoint 없음.
   * confirm endpoint(POST /individual/confirm) 가 동의 완료 + 결과 조회를 통합 처리.
   *
   * NZSC-3074(본인인증 결합): BE contract 확장 — body 에 `userVerifySeq` 필수 포함.
   * 본인인증 결과(/api/v1/user/verification/phone/result/guest) 의 user_verify_seq 를 그대로 전달.
   * BE 가 user_verify 행을 검증해 동일 token + 동일 본인인증 식별번호일 때만 KCB 호출.
   *
   * 응답 status:
   *  - 'completed'  : 결과 포함. 외부 페이지에서는 결과 미노출 (별도 결과 화면으로 redirect).
   *  - null (401, 404, 410, 네트워크 실패) → caller 가 'error' step 으로 전환.
   *
   * NZSC-3072: BE 가 SHERLOCK_CREDIT_INDIVIDUAL_ENABLED 플래그 제거 — 'preparing' 응답 사라짐.
   *
   * agreedTermIds 는 placeholder — BE 가 약관 메타를 저장하지 않음 (NZSC-3057 contract).
   * 향후 audit 요구사항 발생 시 별도 티켓 발행 후 처리.
   **************************************************************/
  async acAgreeConsent({ commit }, { token, agreedTermIds, userVerifySeq }) {
    // agreedTermIds 명시적 미사용 — BE contract 가 받지 않으므로 payload 미포함.
    // FE 에서 동의 여부 검증만 한 뒤 token + userVerifySeq 전송.
    void agreedTermIds;
    const res = await callPostApi({
      url: SherlockAcConstant.POST_SHERLOCK_CREDIT_INDIVIDUAL_CONFIRM,
      data: { token, userVerifySeq },
      commit,
    });
    return res;
  },

  /**************************************************************
   * NZSC-3074: 셜록 confirm — 본인인증 결합용 별칭.
   * `acAgreeConsent` 와 동일한 endpoint 를 호출하되,
   * 본인인증 콜백 페이지에서 의도(약관 동의가 아닌 본인인증 후 결과 트리거)를 명확히 하기 위해 분리.
   *
   * 호출 시점: SherlockKcbVerifyResultView 의 result/guest 성공 직후.
   * payload: { token, userVerifySeq } — agreedTermIds 미포함 (이미 step2 에서 동의 완료).
   **************************************************************/
  async acSherlockConfirmIndividual({ commit }, { token, userVerifySeq }) {
    const res = await callPostApi({
      url: SherlockAcConstant.POST_SHERLOCK_CREDIT_INDIVIDUAL_CONFIRM,
      data: { token, userVerifySeq },
      commit,
    });
    if (res?.status === "completed" && res?.result) {
      commit("SET_INDIVIDUAL_RESULT", res.result);
    }
    return res;
  },

  /**************************************************************
   * NZSC-3074: 셜록 KCB 본인인증 게스트 prepare.
   * 셜록 격리 — 공통 `verifyStore.acVerifyPhonePrepareGuest` 를 import 하지 않고
   * 셜록 모듈 내부에서 별도 호출. service 태그는 `sherlock_credit/{token}`.
   *
   * BE 응답: { code, data: { popup_url, cp_cd, token: mdl_tkn, ... } }
   * 성공 시 popup_url 로 폼 POST submit → KCB 인증 팝업 진입.
   **************************************************************/
  async acVerifyPhonePrepareGuest({ commit }, { service }) {
    const res = await callPostApi({
      url: SherlockAcConstant.POST_VERIFY_PHONE_PREPARE_GUEST,
      data: { provider: "kcb", service },
      commit,
    });
    if (res) {
      commit("SET_VERIFY_PREPARE", res);
    }
    return res;
  },

  /**************************************************************
   * NZSC-3074: 셜록 KCB 본인인증 게스트 result.
   * KCB 콜백 페이지(SherlockKcbVerifyResultView) 에서 호출.
   * BE 가 mdl_tkn 으로 KCB 결과를 조회 + user_verify row 생성 + user_verify_seq(result_ref) 반환.
   *
   * BE 응답: { code: 0, data: { result_code: 'B000', result_msg, result_ref, ... } }
   *  - result_code='B000' → 성공 (result_ref 가 user_verify_seq)
   *  - 그 외 → 실패 (result_msg 노출)
   **************************************************************/
  // eslint-disable-next-line no-unused-vars
  async acVerifyPhoneResultGuest({ commit }, { token }) {
    // token: mdl_tkn (KCB 가 콜백 query string 으로 돌려준 값).
    // [hotfix 2026-05-19] user_no: "SHERLOCK_SUBJECT" 박아야 BE resultGuest 화이트리스트 통과.
    //   typhoon 흐름은 "GUEST_TYPHOON" (UserVerifyKcbResultCreditTyphoonGuestView.vue:49 참조).
    //   누락 시 BE UserVerifyRequestDto 의 user_no 필수 검증 실패 → 400 "user_no must be a string".
    const res = await callPostApi({
      url: SherlockAcConstant.POST_VERIFY_PHONE_RESULT_GUEST,
      data: { provider: "kcb", token, user_no: "SHERLOCK_SUBJECT" },
      commit,
    });
    return res;
  },

  /**************************************************************
   * NZSC-3074: 셜록 KCB 개인 신용 결과 영구 조회.
   * confirm 호출 이후 결과 페이지(/sherlock/credit/individual/result/:token) 에서 호출.
   * BE 정책: used_date 무관 — 동일 token 에 대해 한 건 영구 접근 (만료 7일 BE 적용).
   *
   * 응답: { subjectName, masked: { ... } } (cold-syrup 분석 결과 마스킹).
   *  - null (404 무효 / 410 만료) → caller 가 만료 안내 화면 표시.
   **************************************************************/
  async acGetSherlockKcbCreditResult({ commit }, { token }) {
    if (!token) return null;
    const res = await callGetApi({
      url: `${SherlockAcConstant.GET_SHERLOCK_CREDIT_INDIVIDUAL_RESULT}/${encodeURIComponent(
        token,
      )}`,
      commit,
    });
    if (res) {
      commit("SET_INDIVIDUAL_RESULT", res);
    }
    return res;
  },

  /**************************************************************
   * NZSC-3076: 요청자(셜록 회원) 이력 row 클릭 → 결과 페이지 재진입.
   *
   * BE NZSC-3075: GET /api/v1/sherlock/credit/:seq
   *  - AuthSherlockGuard 필요 (셜록 회원 본인 이력만 접근).
   *  - 30일 영구 접근 (BE 가 createDate 기준 차단). 만료 시 410.
   *  - 다른 회원 seq 접근 시 401/403 (BE 가 user_no 매칭 검증).
   *
   * 응답 contract (BE NZSC-3075 와 정렬):
   *  - 200: { type, targetName, targetIdentifier, queriedAt, result } — SherlockDelinquencyResult props 와 1:1.
   *  - 401/403/410/404 → apiWrapper 가 null 반환. caller(SherlockDelinquencyResultPage) 가 이력 탭으로 redirect.
   *
   * SET_INDIVIDUAL_RESULT / SET_CORPORATE_RESULT 에 commit 하지 않고 응답만 반환.
   * 사유: 이력 재진입은 단일 페이지에서 즉시 표시하는 흐름. store 영속화 시 다른 탭 컴포넌트와 충돌 가능.
   **************************************************************/
  async acGetSherlockCreditBySeq({ commit }, { seq }) {
    if (!seq) return null;
    const res = await callGetApi({
      url: `${SherlockAcConstant.GET_SHERLOCK_CREDIT_BY_SEQ}/${encodeURIComponent(seq)}`,
      commit,
    });
    return res;
  },

  /**************************************************************
   * NZSC-3120 — 셜록 체납·연체 결과 PDF 다운로드.
   *
   * GET /v1/sherlock/credit/:seq/pdf — responseType blob.
   * BE 는 api-admin internal endpoint(puppeteer) 위임. AuthSherlockGuard + 발급 본인 검증 그대로.
   *
   * 반환: { blob, filename } 또는 null (실패 시 caller 가 토스트/inline 에러 처리).
   * blob 응답이라 apiWrapper 우회 (apiWrapper 는 JSON envelope 가정).
   **************************************************************/
  async acDownloadSherlockCreditPdf(_, { seq }) {
    if (!seq) return null;
    try {
      const res = await api.get(
        `${SherlockAcConstant.GET_SHERLOCK_CREDIT_BY_SEQ}/${encodeURIComponent(seq)}/pdf`,
        { responseType: "blob" },
      );
      const filename = fnParseDispositionFilename(
        res.headers?.["content-disposition"] || res.headers?.["Content-Disposition"],
      );
      return { blob: res.data, filename };
    } catch (err) {
      return null;
    }
  },

  /**************************************************************
   * 조회/발급 이력 리스트
   **************************************************************/
  async acGetHistory({ commit }, { page = 1, pageSize = 20, type } = {}) {
    const res = await callGetApi({
      url: SherlockAcConstant.GET_SHERLOCK_CREDIT_HISTORY,
      params: { page, pageSize, type },
      commit,
    });
    if (res) {
      // [hotfix 2026-05-18] BE→FE contract drift 매핑.
      //   BE items: { subjectName, subjectNo, status: lowercase, ... }
      //   FE expects: { targetName, targetIdentifier, status: UPPERCASE }
      //   NZSC-3058/3057 contract 정렬에서 누락된 매핑.
      const STATUS_MAP = {
        requested: "PENDING",
        consent_pending: "PENDING",
        completed: "COMPLETED",
        failed: "FAILED",
        expired: "EXPIRED",
      };
      const mappedItems = (res.items || []).map(item => ({
        ...item,
        targetName: item.targetName ?? item.subjectName ?? "",
        targetIdentifier: item.targetIdentifier ?? item.subjectNo ?? "",
        status: STATUS_MAP[item.status] || item.status,
      }));
      commit("SET_HISTORY_LIST", {
        items: mappedItems,
        totalCount: res.totalCount || 0,
        page,
        pageSize,
      });
      return { ...res, items: mappedItems };
    }
    return res;
  },

  /**************************************************************
   * 포인트 차감 처리 — TODO: NZSC-3047 셜록 구독형 가격제와 합쳐 활성화.
   * 이번 PR은 UI 만 노출하고 실제 차감 API 호출은 하지 않음.
   * eslint-disable: 명시적으로 미사용 (의도된 비활성).
   **************************************************************/
  // eslint-disable-next-line no-unused-vars
  async acProcessPointDeduction({ commit }, payload) {
    // TODO(NZSC-3047): 셜록 구독형 가격제 합류 후 활성화.
    //   const res = await callPostApi({ url: '...', data: payload, commit });
    //   return res;
    console.warn(
      "[sherlock-delinquency] acProcessPointDeduction is disabled (NZSC-3054 + NZSC-3047 cutover).",
    );
    return { code: 0, dryRun: true };
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
